import type { HTTPError } from "ky";

export interface ApiError {
  status: number;
  message: string;
  details?: unknown;
}

function isHttpError(error: unknown): error is HTTPError {
  return (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    "request" in error
  );
}

export function isAbortError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === "AbortError" ||
      error.message === "The operation was aborted")
  );
}

function extractDetailMessage(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  if (!("detail" in payload)) return null;

  const detail = payload.detail;
  if (typeof detail === "string" && detail.trim().length > 0) {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (
          item &&
          typeof item === "object" &&
          "msg" in item &&
          typeof item.msg === "string"
        ) {
          return item.msg;
        }
        return null;
      })
      .filter((item): item is string =>
        Boolean(item && item.trim().length > 0),
      );

    if (messages.length > 0) {
      return messages.join("; ");
    }
  }

  return null;
}

export async function toApiError(error: unknown): Promise<ApiError> {
  if (isHttpError(error)) {
    const payload = await error.response.json().catch(() => null);
    const payloadError =
      payload && typeof payload === "object" && "error" in payload
        ? payload.error
        : null;
    const payloadDetail = extractDetailMessage(payload);
    const message =
      (typeof payloadError === "string" && payloadError.trim().length > 0
        ? payloadError
        : null) ??
      payloadDetail ??
      error.message;
    return {
      status: error.response.status,
      message,
      details: payload,
    };
  }

  if (
    error instanceof SyntaxError &&
    /Unexpected token '<'/.test(error.message)
  ) {
    return {
      status: 0,
      message: "Connessione con il server fallita.",
    };
  }

  return {
    status: 0,
    message:
      error instanceof Error
        ? error.message
        : "Connessione con il server fallita.",
  };
}
